/**
 * @file throttle-error.ts
 * @description Erreur typée partagée levée quand un appel HTTP reste en 429
 * (Too Many Requests) après épuisement des tentatives de backoff.
 *
 * Centralise ce que `GraphThrottleError` apportait historiquement, pour tous les
 * providers (Graph, Google, Stripe…) :
 *  - Remplace les `throw 'Received 429…'` (string brute, sans stack).
 *  - Donne un point de détection FIABLE en amont (`err instanceof ThrottleError`
 *    ou duck-typing `err.isThrottle === true`) pour que les filets de retry manuels
 *    en amont (AgendaManager, MailManager…) ne rejouent PAS sur un 429 déjà géré
 *    exhaustivement plus bas.
 *
 * Le flag `isThrottle` permet une détection sans import (duck-typing), utile pour
 * les couches qui ne doivent pas dépendre d'un package provider précis.
 */
export class ThrottleError extends Error {
    /** Status HTTP à l'origine de l'erreur (toujours 429). */
    public readonly status = 429 as const;
    /** Marqueur duck-typed pour détection sans import. */
    public readonly isThrottle = true as const;
    /** Endpoint / libellé concerné. */
    public readonly endpoint: string;
    /** Nombre de tentatives de retry effectuées avant abandon. */
    public readonly attempts: number;

    constructor(endpoint: string, attempts: number, message?: string) {
        super(
            message ??
                `Throttled (429) on ${endpoint} after ${attempts} retr${attempts === 1 ? "y" : "ies"}`,
        );
        this.name = "ThrottleError";
        this.endpoint = endpoint;
        this.attempts = attempts;

        // Restaure la chaîne de prototype (TS cible es2015 ; extends Error)
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

/** Détection duck-typed d'une erreur de throttle (429), sans dépendre du type concret. */
export function isThrottleError(error: unknown): boolean {
    return !!error && (error as { isThrottle?: unknown }).isThrottle === true;
}
